// SPDX-License-Identifier: Apache-2.0

import { jsonMember, jsonObject } from "typedjson";

// LPPolicy mirrors the backend's LP cell policy (message/liquidity.go). All
// numeric capacity/volume fields are uint64 string-encoded to avoid float
// corruption; feeRateBps/policyFlags/policyVersion fit in JS numbers.
@jsonObject()
export class LPPolicy {
  @jsonMember(String) maxTradingVolume: string;
  @jsonMember(Number) feeRateBps: number;
  @jsonMember(Number) policyFlags: number;
  @jsonMember(Number) policyVersion: number;
  // Safe-price band, Q64.64 decimal strings (a u128 does not survive a JSON
  // number). Frozen into the cell at creation; the backend refuses a CKB
  // deposit without it.
  @jsonMember(String, { isRequired: false }) safePriceMinX64?: string;
  @jsonMember(String, { isRequired: false }) safePriceMaxX64?: string;

  constructor(
    maxTradingVolume = "0",
    feeRateBps = 0,
    policyFlags = 0,
    policyVersion = 1,
    safePriceMinX64?: string,
    safePriceMaxX64?: string,
  ) {
    this.maxTradingVolume = maxTradingVolume;
    this.feeRateBps = feeRateBps;
    this.policyFlags = policyFlags;
    this.policyVersion = policyVersion;
    this.safePriceMinX64 = safePriceMinX64;
    this.safePriceMaxX64 = safePriceMaxX64;
  }
}

// LPCellInfo is one CKB LP cell as returned by GetLPPositions. The shannon
// amounts and nonce are uint64 string-encoded — handle them as BigInt, never
// Number.
@jsonObject()
export class LPCellInfo {
  @jsonMember(String) poolID: string;
  @jsonMember(String) outpoint: string;
  @jsonMember(String) ownerLockHash: string;
  @jsonMember(String) operatorLockHash: string;
  // ETH address credited with ETH-pool shares when this cell's traded CKB is
  // converted. Owner-designated at creation and frozen on-chain thereafter.
  @jsonMember(String) ethBeneficiary: string;
  @jsonMember(String) availableCKB: string;
  @jsonMember(String) reservedCKB: string;
  @jsonMember(String) cumulativeFeesEarnedCKB: string;
  @jsonMember(String) nonce: string;
  @jsonMember(Boolean) active: boolean;
  @jsonMember(LPPolicy) policy: LPPolicy;

  constructor(
    poolID = "",
    outpoint = "",
    ownerLockHash = "",
    operatorLockHash = "",
    ethBeneficiary = "",
    availableCKB = "0",
    reservedCKB = "0",
    cumulativeFeesEarnedCKB = "0",
    nonce = "0",
    active = false,
    policy: LPPolicy = new LPPolicy(),
  ) {
    this.poolID = poolID;
    this.outpoint = outpoint;
    this.ownerLockHash = ownerLockHash;
    this.operatorLockHash = operatorLockHash;
    this.ethBeneficiary = ethBeneficiary;
    this.availableCKB = availableCKB;
    this.reservedCKB = reservedCKB;
    this.cumulativeFeesEarnedCKB = cumulativeFeesEarnedCKB;
    this.nonce = nonce;
    this.active = active;
    this.policy = policy;
  }
}

// ETHPoolCall is the call metadata the backend returns for ETH deposit/withdraw.
// The wallet (MetaMask) performs the actual LiquidityPool contract call; only
// these fields travel on the wire. value (wei) is set for deposit, shares for
// withdraw.
@jsonObject()
export class ETHPoolCall {
  @jsonMember(String) poolAddress: string;
  @jsonMember(String) chainID: string;
  @jsonMember(String) method: string;
  @jsonMember(String, { isRequired: false }) value?: string;
  @jsonMember(String, { isRequired: false }) shares?: string;

  constructor(
    poolAddress = "",
    chainID = "",
    method = "",
    value?: string,
    shares?: string,
  ) {
    this.poolAddress = poolAddress;
    this.chainID = chainID;
    this.method = method;
    this.value = value;
    this.shares = shares;
  }
}
